/**
 * @fileoverview
 * Implementation of BMaps 3D visualization with 3Dmol.js
 * Original 3Dmol implementation: 3dmol_interface.js
 * Note that this depends on custom changes made to 3dmol code, especially the addition of
 * the internal atom property.
 *
 * @typedef {import('3dmol-bmaps/src').LabelSpec} LabelSpec
 */

import { VisualizerInterface } from './VisualizerInterface';
import {
    GetChainColor, SelectionColor, ColorStyles, MaestroLikeColors, secondaryStructureColor,
} from './themes';
import {
    MolStyles, isRepSetStyle, repsOfStyle, repSetOptions, transparencyByRep,
} from './style_manager';
import { getFullResidueId } from './util/mol_info_utils';
import { isNeutralCamera, neutralFrom3Dmol, threeDmolFromNeutral } from './visualizerCamera';
import {
    assertValidView, rotateViewAboutPoint, zoomView, panView,
} from './util/viewTransform3Dmol';

/**
 * 3Dmol cartoon spec of each rep-set cartoon type (style_manager RepSetCartoonTypes), over the
 * cartoon style's own. Loop, tube and putty (every residue a round tube in PyMOL; putty's width
 * follows the B-factor) are 3Dmol traces.
 */
const CartoonTypeSpecs3Dmol = {
    automatic: {},
    loop: { style: 'trace', thickness: 0.25 },
    tube: { style: 'trace', thickness: 0.6 },
    putty: { style: 'trace', thickness: 0.6 }, // no B-factor width in 3Dmol: a tube
    rectangle: { style: 'rectangle', arrows: false },
    oval: { style: 'oval', arrows: false },
};

let $3Dmol;

// Protect require of 3Dmol.js during Next.js SSR / SSG
if (typeof window !== 'undefined') {
    // Import 3Dmol-bmaps from local file
    // eslint-disable-next-line global-require
    // $3Dmol = require('../lib/js/3Dmol-bmaps');

    // Import 3Dmol-bmaps from npm package
    // eslint-disable-next-line global-require
    $3Dmol = require('3dmol-bmaps/build/3Dmol-min');
    $3Dmol.setSyncSurface(true); // See BP2-131. 3Dmol had fixed this, but similar error in 2023.
}

/** @returns {{cameraX: number, cameraZ: number, fov: number}} a GLViewer's fixed camera setup */
function cameraSetupOf(viewer) {
    return {
        cameraX: Number.isFinite(viewer.camerax) ? viewer.camerax : 0,
        cameraZ: Number.isFinite(viewer.CAMERA_Z) ? viewer.CAMERA_Z : 150,
        fov: Number.isFinite(viewer.fov) ? viewer.fov : 20,
    };
}

/** The backend-neutral camera (visualizerCamera.js) of a GLViewer's current view. */
function neutralCameraOf(viewer) {
    const { position } = viewer.rotationGroup;
    return neutralFrom3Dmol({
        modelPosition: viewer.modelGroup.position,
        rotationPosition: { x: position.x || 0, y: position.y || 0, z: position.z },
        quaternion: viewer.rotationGroup.quaternion,
        ...cameraSetupOf(viewer),
    });
}

function applyNeutralCamera(viewer, neutral) {
    const {
        modelPosition, rotationPosition, quaternion,
    } = threeDmolFromNeutral(neutral, cameraSetupOf(viewer));
    Object.assign(viewer.modelGroup.position, modelPosition);
    Object.assign(viewer.rotationGroup.position, rotationPosition);
    Object.assign(viewer.rotationGroup.quaternion, quaternion);
}

/**
 * Restate 3Dmol's 'zoom' event in the convention VisualizerInterface.RegisterMouseHandler
 * requires: `detail.delta` is the wheel's own deltaY (negative = wheel up / pinch out).
 *
 * The 3dmol-bmaps fork (GLViewer._handleUserZoom) reports how far the CAMERA moved instead, which
 * is three steps removed from the gesture: 3Dmol's plain wheel zooms out on wheel up, it inverts
 * that while Ctrl is held (its trackpad-pinch handling), and the camera does not move at all at
 * the zoom limit. The original wheel event rides along as `detail.sourceEvent`, so read it there.
 *
 * A zoom that did not come from a wheel -- 3Dmol also raises one for Shift+drag, and Ctrl+Shift+
 * drag steps the binding-site radius -- has no deltaY and keeps 3Dmol's camera delta. That is
 * deliberate, not a gap: the drag path has no Ctrl inversion and only fires on a real move, so
 * its sign already gives the drag the direction it has always had (camera moving away = larger).
 *
 * A touch-screen pinch has no deltaY either, but its camera delta is positive when spreading, the
 * opposite of a trackpad pinch's deltaY, so it is negated.
 */
function inWheelConvention(zoomEvent) {
    const source = zoomEvent.detail?.sourceEvent;
    const deltaY = source?.deltaY;
    if (Number.isFinite(deltaY)) {
        return new CustomEvent('zoom', { detail: { ...zoomEvent.detail, delta: deltaY } });
    }
    if (source?.targetTouches?.length === 2) {
        return new CustomEvent('zoom', { detail: { ...zoomEvent.detail, delta: -zoomEvent.detail.delta } });
    }
    return zoomEvent;
}

export class Visualizer3Dmol extends VisualizerInterface {
    static get backendName() { return '3dmol'; }

    constructor(options) {
        super(options);
        this.state = {
            colorByChainId: true, // setting?
            proteinChains: [],
            oldHovered: null,
            sphereCount: 0, // 3dmol workaround
            /** @type {import('3dmol-bmaps/src').GLModel} */
            currentModel: null,
        };
        this.MouseCallback = this.MouseCallback.bind(this);
        this.HoverCallback = this.HoverCallback.bind(this);
        this.UnHoverCallback = this.UnHoverCallback.bind(this);
        this.Reset = this.Reset.bind(this);
    }

    // VIRTUAL METHOD IMPLEMENTATION

    // Setup
    InitViewer(options) {
        const { parentEltId, backgroundColor, cartoonQuality } = options;
        const viewerOptions = { defaultcolors: $3Dmol.elementColors.rasmol };
        if (cartoonQuality != null) {
            viewerOptions.cartoonQuality = cartoonQuality;
        }
        /** @type {import('3dmol-bmaps/src').GLViewer} */
        this.viewer = $3Dmol.createViewer(parentEltId, viewerOptions);
        this.viewer.userContextMenuHandler = (target, x, y, evt) => {
            this.MouseCallback(target, this.viewer, evt);
        };
        this.viewer.getCanvas().onmouseleave = () => {
            // Prevent hover events from firing after mouse leaves the canvas
            clearTimeout(this.viewer.hoverTimeout);
        };
        this.InstallMetaKeyPan();
        console.log('Created 3d viewer');
        if (backgroundColor) {
            // Note: setting the background color here will be overridden
            // by the call to setColorScheme() in main() in main.js.
            this.SetBackgroundColor(backgroundColor);
        }
    }

    /**
     * Pan with Meta (Cmd) + left-drag, in addition to 3Dmol's Ctrl + left-drag.
     * macOS turns Ctrl-click into a right click, so Ctrl-drag pan is unusable on a
     * Mac trackpad. Listeners go on the container in the capture phase so 3Dmol's
     * canvas-level handlers, which would rotate instead, never see the event.
     */
    InstallMetaKeyPan() {
        let lastPos = null;

        const onMove = (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            this.viewer.translateScene(evt.pageX - lastPos.x, evt.pageY - lastPos.y);
            lastPos = { x: evt.pageX, y: evt.pageY };
        };
        const onUp = () => {
            lastPos = null;
            window.removeEventListener('mousemove', onMove, true);
            window.removeEventListener('mouseup', onUp, true);
        };

        this.viewer.container.addEventListener('mousedown', (evt) => {
            // The container is shared with other visualizers (live switching): only while active.
            if (this.active === false || !evt.metaKey || evt.button !== 0) return;
            evt.preventDefault();
            evt.stopPropagation();
            lastPos = { x: evt.pageX, y: evt.pageY };
            // Track on the window so the pan survives the cursor leaving the canvas.
            window.addEventListener('mousemove', onMove, true);
            window.addEventListener('mouseup', onUp, true);
        }, true);
    }

    Reset() {
        this.state.proteinChains = [];
        this.state.oldHovered = null;
        this.state.sphereCount = 0;

        if (this.state.currentModel) {
            console.log('3d visualizer: removing the current model.');
            this.UnmapAllVisAtoms();
            // Removes bonds too.
            this.viewer.removeModel(this.state.currentModel); // ok if not yet defined
            this.state.currentModel = undefined;
        }
        this.Redisplay();
    }

    /**
     * Live switching: empty and hide. Reset() only removes the model; 3Dmol's clear() removes
     * the shapes, labels and surfaces too, so nothing stale reappears on a later Activate().
     * The instance stays: 3Dmol keeps window/document listeners that cannot be removed.
     */
    Deactivate() {
        this.active = false;
        this.Reset();
        this.viewer.clear();
        this.viewer.getCanvas().style.display = 'none';
    }

    Activate() {
        this.active = true;
        this.viewer.getCanvas().style.display = '';
        this.viewer.resize();
        this.Redisplay();
    }

    MapAtoms(appAtom, visAtom) {
        appAtom.forVisualizer = visAtom;
    }

    UnmapAtom(appAtom) {
        appAtom.forVisualizer = null;
    }

    UnmapAtoms(appAtoms) {
        appAtoms.forEach(this.UnmapAtom);
    }

    VisAtom(appAtom) {
        return appAtom.forVisualizer;
    }

    AppAtom(visAtom) {
        return visAtom.appAtom;
    }

    UnmapAllVisAtoms() {
        const { atoms: visAtoms } = this.state.currentModel.getInternalState();
        for (const visAtom of visAtoms) {
            const appAtom = this.AppAtom(visAtom);
            if (appAtom) this.UnmapAtom(appAtom);
        }
    }

    /**
     * `'3DMol'`: this backend's own token (exact round trip). `neutral`: the backend-neutral
     * camera (visualizerCamera.js), used by another backend or after a live switch.
     */
    GetCameraPosition() {
        return {
            neutral: neutralCameraOf(this.viewer),
            '3DMol': {
                rotationGroup: {
                    z: this.viewer.rotationGroup.position.z,
                    quaternion: {
                        ...this.viewer.rotationGroup.quaternion,
                    },

                },
                modelGroup: {
                    ...this.viewer.modelGroup.position,
                },
            },
        };
    }

    SetCameraPosition(camera) {
        if (!camera) return;
        if (!camera['3DMol']) {
            if (isNeutralCamera(camera.neutral)) applyNeutralCamera(this.viewer, camera.neutral);
            return;
        }
        this.viewer.rotationGroup.position.z = camera['3DMol'].rotationGroup.z;
        this.viewer.rotationGroup.quaternion.x = camera['3DMol'].rotationGroup.quaternion.x;
        this.viewer.rotationGroup.quaternion.y = camera['3DMol'].rotationGroup.quaternion.y;
        this.viewer.rotationGroup.quaternion.z = camera['3DMol'].rotationGroup.quaternion.z;
        this.viewer.rotationGroup.quaternion.w = camera['3DMol'].rotationGroup.quaternion.w;
        this.viewer.modelGroup.position.x = camera['3DMol'].modelGroup.x;
        this.viewer.modelGroup.position.y = camera['3DMol'].modelGroup.y;
        this.viewer.modelGroup.position.z = camera['3DMol'].modelGroup.z;
    }

    /**
     * The `CartoonStyle` preference changes nothing on 3Dmol, by design (owner decision
     * 2026-09-26). "BMaps" is BMaps' usual look, which is what BMaps' 3Dmol fork draws by
     * default: its drawStrand (lib/js/3Dmol-bmaps.js) already narrows the cartoon to helix/sheet
     * 0.8, coil 0.1, thickness 0.2 (upstream 1.3 / 0.5 / 0.4). And "PyMOL"'s proportions are
     * unreachable here: 3Dmol applies one `cartoon.width` to loops, helices and sheets alike,
     * so PyMOL's 1.35 would fatten every loop. Kept explicit, over the base class's no-op, for
     * the record; the Maestro-derived 0.15 Å ribbons and 0.16 Å sticks apply to PyMOL only.
     */
    SetCartoonStyle() { }

    /**
     * The `ColorStyle` preference (themes.js ColorStyles). Takes effect on the next redraw (the
     * caller's job): the default colours are decided in setAtomColor, per atom.
     */
    SetColorStyle(name) {
        this.colorStyle = name === ColorStyles.maestro ? ColorStyles.maestro : ColorStyles.bmaps;
    }

    /**
     * The cartoon colour of an atom that has no colour of its own: its chain's colour, or under
     * the Maestro-like colouring its secondary structure's (3Dmol's `ss`: 'h' / 's' / none).
     */
    defaultCartoonColor(myAtom) {
        if (this.colorStyle === ColorStyles.maestro) return secondaryStructureColor(myAtom.ss);
        return this.getCartoonColorForChain(myAtom.chain);
    }

    GetImageBlob({ type, quality } = {}) {
        const canvas = this.viewer?.getCanvas();
        if (!canvas) return Promise.resolve(null);
        return new Promise((resolve) => {
            // defaults to an image/png type blob
            canvas.toBlob((blob) => resolve(blob), type, quality);
        });
    }

    // Display
    ResizeViewer(width, height) {
        // const target = options.target || this;
        // 3dmol remembers its own w, h
        // if (target)
        this.viewer.resize();
    }

    SetBackgroundColor(colorInfoIn) {
        let colorInfo = colorInfoIn;
        if (typeof (colorInfo) === 'object' && colorInfo.hex != null) {
            colorInfo = colorInfo.hex;
        }
        this.viewer.setBackgroundColor(colorInfo);
    }

    RedisplayViewer() {
        this.viewer.resetModel(this.state.currentModel);
        this.viewer.render();
    }

    /**
     * Rotate the camera by `degrees` about `axis`, holding `pivot` fixed on screen.
     *
     * Deliberately does NOT call viewer.rotate(). That call writes only the quaternion and
     * always pivots about whatever sits at the view centre (-modelGroup.position), so it can
     * never rotate about the molecule. Moving the pivot means moving modelGroup.position in the
     * same step, which is what rotateViewAboutPoint does over the public 8-number view vector.
     *
     * setView() renders: it calls show() internally, which does setSlabAndFog() + render().
     * Note this is the opposite of the neighbouring SetCameraPosition, which pokes
     * rotationGroup/modelGroup directly and relies on its caller to redraw. Do not copy that one.
     *
     * @param {number} degrees rotation angle, positive = right-handed about `axis`
     * @param {'x'|'y'|'z'|[number,number,number]} axis axis in the model's own frame
     * @param {[number,number,number]} [pivot] model-space point to hold fixed; defaults to the
     *        view's current pivot, which makes the call orientation-only
     */
    RotateView(degrees, axis, pivot) {
        this.viewer.setView(rotateViewAboutPoint(this.viewer.getView(), degrees, axis, pivot));
    }

    /**
     * Zoom the camera by `factor`. factor > 1 zooms IN -- 3Dmol's own convention
     * (GLViewer.zoom, :1967-1969), kept so this and viewer.zoom() agree exactly.
     *
     * CAMERA_Z is derived from the live viewer rather than hard-coded as 150:
     * getPerceivedDistance() returns CAMERA_Z - rotationGroup.position.z (:4683), and view[3] IS
     * rotationGroup.position.z, so the sum is CAMERA_Z -- and stays correct after
     * setCameraParameters has changed it (:908-909). CAMERA_Z is `private` in the TypeScript
     * source, so reading this.viewer.CAMERA_Z would work at runtime but reaches past the API.
     *
     * setView() renders: it calls show() internally, which does setSlabAndFog() + render(). See
     * RotateView -- do not copy the neighbouring SetCameraPosition, which does not render.
     *
     * @param {number} factor zoom factor; > 1 zooms in, 0 < factor < 1 zooms out
     */
    ZoomView(factor) {
        const view = this.viewer.getView();
        const cameraZ = this.viewer.getPerceivedDistance() + view[3];
        this.viewer.setView(zoomView(view, factor, cameraZ));
    }

    /**
     * Pan the camera by a model-space displacement, in Angstroms.
     *
     * Deliberately does NOT call viewer.translate() or translateScene(). Those route the offset
     * through screenOffsetToModel (:753-765), which divides by the canvas WIDTH/HEIGHT and then
     * rotates the result into model space -- so the same request would move the structure a
     * different distance on a different-sized window, and in a different direction at every
     * orientation. panView writes modelGroup.position over the public 8-number view instead.
     *
     * setView() renders, unlike the neighbouring SetCameraPosition. See RotateView.
     *
     * @param {[number,number,number]} delta model-frame displacement, in Angstroms
     */
    PanView(delta) {
        this.viewer.setView(panView(this.viewer.getView(), delta));
    }

    /**
     * Put the camera back to the orientation a structure is first drawn in, leaving the FRAMING
     * to ZoomToAtoms.
     *
     * Needed because zoomTo() recomputes modelGroup.position and rotationGroup.position.z but
     * never touches the quaternion. So a framing-only "reset" leaves the molecule at whatever
     * angle it was last dragged to -- which is not the "default orientation" reset-view
     * advertises, and is the first thing a user notices.
     *
     * Uses getView()/setView() rather than poking rotationGroup.quaternion directly: setView
     * renders (show() -> setSlabAndFog() + render()) and it is the same public seam RotateView
     * uses. Only the quaternion is replaced, with the identity; slots 0-3 pass through untouched
     * so the caller's framing survives.
     */
    ResetCameraOrientation() {
        const [x, y, z, zoom] = this.viewer.getView();
        const resetOrientationView = [x, y, z, zoom, 0, 0, 0, 1];
        // Validate the view we are ABOUT TO WRITE, not the old view wholesale. That permits reset
        // to repair a corrupt old quaternion while still refusing non-finite framing components
        // before 3Dmol silently accepts them and blanks the canvas.
        assertValidView(resetOrientationView, 'reset camera view');
        this.viewer.setView(resetOrientationView);
    }

    ZoomToAtoms(selectorFn) {
        if (selectorFn) {
            this.viewer.zoomTo({ predicate: this.wrapAtomFn(selectorFn) });
        } else {
            this.viewer.zoomTo();
        }
    }

    // Create a 3dmol atom object from a bmaps atom object
    MakeAtom(atom) {
        const [x, y, z] = atom.getPosition();
        const visAtom = {
            x,
            y,
            z,
            appAtom: atom,
            resn: atom.resn,
            resname: atom.resname,
            elem: atom.elem,
            hetflag: atom.hetflag,
            resi: atom.resi,
            icode: atom.icode,
            chain: atom.chain,
            modelNum: atom.modelNum,
            rescode: atom.rescode,
            serial: atom.serial,
            atom: atom.atom,
            amber: atom.amber,
            index: atom.index,
            bonds: atom.bondedAtoms.map((a2) => a2.index),

            bondOrder: atom.bondOrder,
            properties: atom.properties,
            b: atom.b,
            pdbline: atom.pdbline,
            style: atom.initialStyle
                ? this.makeStyleSpec(atom.initialStyle)
                : $3Dmol.GLModel.defaultAtomStyle,

            ss: 'c',
            intersectionShape: {
                sphere: [], cylinder: [], line: [], triangle: [],
            },
            color: $3Dmol.elementColors.defaultColors[atom.elem]
                || $3Dmol.elementColors.defaultColor,
            clickable: true,
            callback: this.MouseCallback,
            hoverable: true,
            hover_callback: this.HoverCallback,
            unhover_callback: this.UnHoverCallback,
            contextMenuEnabled: true,
        };

        if (!atom.hetflag) {
            this.updateProteinAtom(atom, visAtom);
        }

        return visAtom;
    }

    // MakeBond() - 3Dmol implementation doesn't need it

    // Load a list of bmaps atoms into 3Dmol
    AddAtoms(atoms, bonds) {
        if (!this.state.currentModel) {
            console.log('Adding a new model.');
            this.state.currentModel = this.viewer.addModel();
        }

        const newAtoms = atoms.map((a) => this.MakeAtom(a));
        this.state.currentModel.addAtoms(newAtoms);
        this.refreshMaps();
        this.updateAtomBonds(atoms);
        return newAtoms;
    }

    /**
     * After adding atoms to 3Dmol, ensure that bond definitions are complete and correct:
     *     `bond` is an array of indices to other 3Dmol atoms.
     *     `bondOrder` also needs to be updated.
     * 3Dmol updates indices when adding atoms, and automatically adjusts bond lists to use the new
     * indices. But since we add atoms one atomgroup at a time, interresidue bonds are tricky.
     * This function will ensures that the correct bond indices in all 3Dmol atom bond lists.
     *
     * Note: this requires up-to-date mappings from bmaps atoms to 3Dmol atoms (via refreshMaps).
     * @param {[Atom]} bmapsAtoms
     */
    updateAtomBonds(bmapsAtoms) {
        const bmapsBonds = new Set();
        bmapsAtoms.forEach((at) => at.getBonds().forEach((b) => bmapsBonds.add(b)));

        for (const bmapsBond of bmapsBonds) {
            const [bmapsAtomA, bmapsAtomB] = bmapsBond.getAtoms();
            const canvasAtomA = this.VisAtom(bmapsAtomA);
            const canvasAtomB = this.VisAtom(bmapsAtomB);
            if (canvasAtomA && canvasAtomB) {
                // We can only update canvas atoms if they are both present, since we need the
                // bond list of one and index of the other. If one canvas atom is missing, we
                // won't display the bond now. It will get shown if the atom appears later.
                if (!canvasAtomA.bonds.includes(canvasAtomB.index)) {
                    canvasAtomA.bonds.push(canvasAtomB.index);
                    canvasAtomA.bondOrder.push(bmapsBond.order);
                }

                if (!canvasAtomB.bonds.includes(canvasAtomA.index)) {
                    canvasAtomB.bonds.push(canvasAtomA.index);
                    canvasAtomB.bondOrder.push(bmapsBond.order);
                }
            }
        }
    }

    updateProteinAtom(appAtom, visAtom) {
        // Keep track of chains. This is for coloring in order. I think.
        const chain = appAtom.chain;
        if (chain && !this.state.proteinChains.includes(chain)) {
            this.state.proteinChains.push(chain);
        }

        // Apply helix / sheet info
        const proteinResidue = VisualizerInterface.getAtomProteinResidue(appAtom);
        const structureInfo = proteinResidue?.getSecondaryStructureInfo();
        if (structureInfo) {
            this.applyStructureFeatures(visAtom, structureInfo);
        }
    }

    applyStructureFeatures(atom, structureInfo) {
        if (!structureInfo) return;

        const { type, isStart, isEnd } = structureInfo;
        const myType = { helix: 'h', sheet: 's' }[type];
        atom.ss = myType;
        if (isStart) atom.ssbegin = isStart;
        if (isEnd) atom.ssend = isEnd;
    }

    // Make a short-lived molecule with no interactivity
    MakeTempMolecule(atoms, style, colorMap={}, opacity, atomPair) {
        const tempModel = this.viewer.addModel();

        const newAtoms = atoms.map((a) => {
            const newAtom = this.MakeAtom(a);
            const styleSpec = style ? this.makeStyleSpec(style) : newAtom.style;
            for (const spec of Object.values(styleSpec)) {
                spec.opacity = opacity;
            }
            return {
                ...newAtom,
                style: styleSpec,
                color: colorMap[newAtom.elem] || newAtom.color,
                clickable: false,
                callback: undefined,
                hoverable: true,
                hover_callback: this.HoverCallback,
                unhover_callback: this.UnHoverCallback,
            };
        });

        tempModel.addAtoms(newAtoms);
        if (atomPair && atomPair[0] && atomPair[1]) {
            const atom1Pos = atomPair[0].getPosition({ as: 'object' });
            const atom2Pos = atomPair[1].getPosition({ as: 'object' });
            tempModel.dashedCyl = this.drawCylinder(atom1Pos, atom2Pos, 0.25, 'cyan');
        }
        return tempModel;
    }

    UpdateTempMolecule(tmp, { style, opacity, color }) {
        if (color) console.warn('Visualizer3Dmol UpdateTempMolecule does not yet handle colors');

        const styleSpec = style ? this.makeStyleSpec(style) : {};
        for (const spec of Object.values(styleSpec)) {
            spec.opacity = opacity;
        }
        tmp.setStyle(styleSpec);
    }

    RemoveTempMolecule(tempModel) {
        if (tempModel.dashedCyl) this.viewer.removeShape(tempModel.dashedCyl);
        this.viewer.removeModel(tempModel);
    }

    RemoveAtoms(bmapsAtomsIn) {
        const bmapsAtoms = [].concat(bmapsAtomsIn);
        const visAtoms = [];
        const bmapsToRemove = [];
        for (const batom of bmapsAtoms) {
            const vatom = this.VisAtom(batom);
            if (vatom) {
                visAtoms.push(vatom);
                bmapsToRemove.push(batom);
            }
        }

        if (visAtoms.length > 0) {
            this.state.currentModel.removeAtoms(visAtoms);
            this.UnmapAtoms(bmapsToRemove);
            this.refreshMaps();
            console.log(`Removed ${visAtoms.length} and unmapped ${bmapsToRemove.length} atoms`);
        }
    }

    /**
     * @description The 3Dmol.js GLModel.removeAtoms function will
     * create new atom objects, so after a remove, we need to remap them.
     */
    refreshMaps() {
        const viewerAtoms = this.state.currentModel.selectedAtoms();
        for (const va of viewerAtoms) {
            const bmapsAtom = this.AppAtom(va);
            if (bmapsAtom) {
                this.MapAtoms(bmapsAtom, va);
            } else {
                console.warn(`Missing BMaps atom for 3dmol atom: Atom ${va.index}: ${va.resname}.${va.resi}:${va.chain} ${va.atom}`);
            }
        }
    }

    // Style

    // Style one atom
    StyleAtom(atom, style) {
        // Modify the atom style directly, instead of calling setStyle.
        const styleSpec = this.makeStyleSpec(style);

        const reps = repsOfStyle(style);
        const cartoonLike = style === 'cartoon' || style === 'tribbons'
            || (!!reps && (reps.includes('cartoon') || reps.includes('ribbon')));
        if (cartoonLike && atom.chain) {
            this.colorCartoonByChain(atom.chain, styleSpec);
        }

        const myAtom = this.VisAtom(atom);
        if (myAtom) {
            myAtom.style = styleSpec;
            // Reset the intersection shape, to be recalculated when needed
            // (matching 3Dmol.js setStyle)
            if (myAtom.clickable || myAtom.hoverable) {
                myAtom.intersectionShape = {
                    sphere: [], cylinder: [], line: [], triangle: [],
                };
            }
        } else if (style !== MolStyles.hidden) {
            // Don't complain if trying to 'hide' a non-existent atom.
            console.error(`Visualizer3Dmol Failed to find vis atom for ${atom.resname}.${atom.resi}:${atom.chain} ${atom.atom}`);
        }
    }

    StyleAtoms(style, selectorFn) {
        const selectSpec = selectorFn ? { predicate: this.wrapAtomFn(selectorFn) } : {};
        const selectDesc = selectorFn || 'all';

        console.log(`Setting the molecule display style for ${selectDesc} atoms to '${style}'.`);
        //--- crosses? lines vs. wireframe, etc.
        //--- need to remove surface, etc.

        if (isRepSetStyle(style)) {
            // as 'cartoon' below when the set has a cartoon / ribbon: colored per protein chain
            const spec = this.makeStyleSpec(style);
            if (spec.cartoon) {
                for (const chain of this.state.proteinChains) {
                    selectSpec.chain = chain;
                    this.viewer.setStyle(selectSpec, this.colorCartoonByChain(chain, spec));
                }
            } else {
                this.viewer.setStyle(selectSpec, spec);
            }
            this.Redisplay();
            return;
        }

        switch (style) {
            case 'hidden':
            case 'wireframe':
            case 'sticks':
            case 'ballandstick':
            case 'spacefill':
            case 'ribbons':
            case 'backbone':
            case 'tribbons':
            case '2dview':
                this.viewer.setStyle(selectSpec, this.makeStyleSpec(style));
                break;
            case 'cartoon': {
                const cartoon = this.makeStyleSpec(style);
                for (const chain of this.state.proteinChains) {
                    selectSpec.chain = chain;
                    this.viewer.setStyle(selectSpec, this.colorCartoonByChain(chain, cartoon));
                }
                break;
            }
            default:
                console.warn(`StyleAtoms: unknown molecule style: ${style}`);
        }
        this.Redisplay();
    }

    setAtomSelected(atom, selected) {
        this.setAtomColor(atom, selected ? SelectionColor : 'default');
        // Old 2d Viewer
        // setAtomColor2d(atom, selected ? SelectionColor : 'default');
    }

    setAtomColor(atom, colorIn) {
        let color = colorIn;
        const myAtom = this.VisAtom(atom);
        if (!myAtom) {
            // Don't complain the requested atom isn't recognized. When receiving selection data
            // BMaps will probably request to recolor all atoms, whether or not they are currently
            // loaded in the 3D canvas.
            return;
        }

        // Note: per 3Dmol.js API, we track cartoon colors separately from atoms colors.
        if (myAtom.style.cartoon && myAtom.style.cartoon.color) {
            const defaultColor = this.defaultCartoonColor(myAtom);
            if (color === 'default' && myAtom.style.cartoon.color !== defaultColor) {
                myAtom.style = this.colorCartoon(defaultColor, myAtom.style);
            } else if (color !== 'default') {
                myAtom.style = this.colorCartoon(color, myAtom.style);
            }
        }

        if (color === 'default') {
            if (this.colorStyle === ColorStyles.maestro && atom.elem === 'C') {
                color = MaestroLikeColors.carbon; // "Element (Light Carbons)"
            } else if (atom.defaultColorMap && atom.defaultColorMap[atom.elem]) {
                color = atom.defaultColorMap[atom.elem];
            } else {
                color = $3Dmol.elementColors.rasmol[atom.elem] || $3Dmol.elementColors.defaultColor;
            }
        }
        myAtom.color = color;
        myAtom.surfaceColor = color;
    }

    atomIsVisible(atom) {
        const myAtom = this.VisAtom(atom);
        return myAtom && myAtom.style && Object.keys(myAtom.style).length > 0;
    }

    // Objects etc

    // Draw a surface
    //     displayAtomsFn - selector function for the atoms whose surface to display
    //     computingAtomsFn - selector function for the atoms to use in computing the surfaces
    //     colorFn - function taking an atom and returning the desired surface color
    //     opacity - percentage of opacity (0-1)
    // Returns: a surfaceId, to be passed to removeSurface()
    DrawSurface({
        atoms, atomGroup, displayAtomsFn, computingAtomsFn, colorFn, opacity=0.8,
    }) {
        let displaySel;
        if (displayAtomsFn) {
            displaySel = { predicate: this.wrapAtomFn(displayAtomsFn) };
        } else if (atoms) {
            // Convert atoms to MyAtoms (visualizer's copy) upfront and only keep that array around
            const myAtoms = atoms.map(this.VisAtom);
            displaySel = { predicate: (a) => myAtoms.includes(a) };
        } else if (atomGroup) {
            displaySel = { predicate: (a) => atomGroup.hasAtom(this.AppAtom(a)) };
        } else {
            throw new Error('DrawSurface: a surface needs to have either atoms, an atom group, or a display selector function defined.');
        }
        const computingSel = computingAtomsFn ? { predicate: this.wrapAtomFn(computingAtomsFn) }
            : displaySel;

        const style = { opacity };
        if (colorFn) {
            style.colorfunc = this.wrapAtomFn(colorFn);
        }
        const surfaceType = $3Dmol.SurfaceType.VDW;
        const surfPromise = this.viewer.addSurface(surfaceType, style, displaySel, computingSel);
        return surfPromise.surfid;
    }

    // Remove a surface from the view
    // surfaceId - the surface id returned by drawSurface() when the surface was created
    RemoveSurface(surfaceId) {
        this.viewer.removeSurface(surfaceId);
    }

    RemoveShape(obj) {
        this.viewer.removeShape(obj);
    }

    // Other objects

    drawLine(start, end, color) {
        const line = this.viewer.addLine({ start, end, color });
        return line;
    }

    removeLine(line) {
        this.viewer.removeShape(line);
    }

    drawSphere(center, radius, color, shapeOptions={}) {
        const spec = {
            center,
            radius,
            color,
        };
        this.prepShape(spec, shapeOptions);
        const sphere = this.viewer.addSphere(spec);
        this.finishShape(sphere, shapeOptions);
        this.sphereCrashWorkaround_Add();
        return sphere;
    }

    removeSphere(sphere) {
        this.sphereCrashWorkaround_Remove();
        this.viewer.removeShape(sphere);
    }

    // start/end: {x: x, y: y, z: z}
    drawCylinder(start, end, radius, color, dashed=false, shapeOptions={}) {
        const cylinderSpec = {
            start,
            end,
            radius,
            color,
            dashed,
            fromCap: 1,
            toCap: 1,
        };

        this.prepShape(cylinderSpec, shapeOptions);
        const cyl = this.viewer.addCylinder(cylinderSpec);
        this.finishShape(cyl, shapeOptions);
        return cyl;
    }

    removeCylinder(cylinderShapeObj) {
        this.viewer.removeShape(cylinderShapeObj);
    }

    // Doesn't seem to be used
    drawCone(start, end, radius, color, shapeOptions={}) {
        const coneSpec = {
            start, end, radius, color,
        };
        this.prepShape(coneSpec, shapeOptions);
        const cone = this.viewer.addCone(coneSpec);
        this.finishShape(cone, shapeOptions);
        return cone;
    }

    removeCone(coneShapeObj) {
        this.viewer.removeShape(coneShapeObj);
    }

    /**
     * Update a shape spec before creation, particularly for mouse events
     */
    prepShape(shapeSpec, { description, onClick }={}) {
        if (description) {
            shapeSpec.hoverable = true;
            shapeSpec.hover_callback = this.HoverCallback;
            shapeSpec.unhover_callback = this.UnHoverCallback;
        }

        if (onClick) {
            shapeSpec.clickable = true;
            shapeSpec.callback = this.MouseCallback;
            shapeSpec.contextMenuEnabled = true;
        }
    }

    /**
     * Update a shape after creation, particularly for opacity, hover text, and business object
     */
    finishShape(shape, {
        opacity=1.0, description, onClick, object,
    }={}) {
        // Opacity: undefined => use default; null => don't assign; else assign
        if (opacity != null) {
            shape.opacity = opacity;
        }
        if (description) shape.description = description;
        if (onClick) shape.onClick = onClick;
        if (object) shape.object = object;
    }

    drawArrow(start, end, radius, color, radiusRatio=1.0, shapeOptions={}) {
        const arrowSpec = {
            start, end, radius, radiusRatio, color,
        };
        this.prepShape(arrowSpec, shapeOptions);
        const arrow = this.viewer.addArrow(arrowSpec);
        this.finishShape(arrow, shapeOptions);
        return arrow;
    }

    removeArrow(arrowShapeObj) {
        this.viewer.removeShape(arrowShapeObj);
    }

    drawLabel({
        text, options, centerFn, centerAtomGroup,
    }) {
        let centerFnArg;
        if (centerFn) {
            centerFnArg = { predicate: this.wrapAtomFn(centerFn) };
        } else if (centerAtomGroup) {
            centerFnArg = { predicate: (at) => centerAtomGroup.hasAtom(this.AppAtom(at)) };
        } else {
            throw new Error('drawLabel: label needs either centerFn or centerAtomGroup to specify where it should be drawn');
        }

        return this.viewer.addLabel(text, options, centerFnArg);
    }

    removeLabel(label) {
        this.viewer.removeLabel(label);
    }

    // Mouse callbacks

    MouseCallback(target, _viewer, event, container) {
        let obj;
        if (target) {
            if (target.atom) {
                obj = { atom: target };
            } else {
                obj = { shape: target };
            }
        } else {
            obj = { background: true };
        }
        try {
            this.publishMouseEvent(obj, event.type === 'zoom' ? inWheelConvention(event) : event);
        } catch (ex) {
            console.error(`Exception in mouse event handler ${event.type}`, ex);
        }
    }

    // Note: These are jquery mouse events coming from 3dmol
    HoverCallback(target, _viewer, event, container) {
        const alreadyHovered = (target === this.state.oldHovered);
        // Hover events come from 3Dmol as 'mousemove' so need to convert to mouseenter
        const eventToSend = alreadyHovered ? event : new MouseEvent('mouseenter', event);
        this.state.oldHovered = target;
        this.MouseCallback(target, _viewer, eventToSend, container);
    }

    // Note: These are jquery mouse events coming from 3dmol
    UnHoverCallback(target, _viewer, event, container) {
        this.state.oldHovered = null;
        // Hover events come from 3Dmol as 'mousemove' so need to convert to mouseleave
        const eventToSend = new MouseEvent('mouseleave', event);
        this.MouseCallback(target, _viewer, eventToSend, container);
    }

    // Style helpers

    /**
     * A rep set (B3 option B) as one 3Dmol style: a 3Dmol atom style holds several representations
     * at once (ballandstick is already {sphere, stick}). Lines are the wireframe style's thin
     * sticks (WebGL lines are 1 px), so sticks win when both are on; spheres are full-size, as in
     * spacefill; cartoon wins over ribbon (one `cartoon` key). surface / mesh / dots are not
     * per-atom in 3Dmol and are left out.
     * The set's transparency goes to sticks, spheres and cartoon, as on PyMOL (lines and ribbon
     * stay opaque there) - but 3Dmol keeps one stick / sphere opacity per model, so those only
     * show it when every atom agrees (PerGroupTransparentReps). Cartoon types map to 3Dmol's
     * cartoon styles (loop and tube: a trace).
     * @param {string[]} reps
     * @param {{transparency: number|object, cartoonType: string}} options transparency: one for
     *     every rep, or per rep (style_manager transparencyByRep)
     */
    makeRepSetSpec(reps, { transparency, cartoonType }) {
        const spec = {};
        const byRep = transparencyByRep(transparency);
        const opacityOf = (rep) => (byRep[rep] > 0 ? { opacity: 1 - byRep[rep] } : {});
        if (reps.includes('sticks')) spec.stick = opacityOf('sticks');
        else if (reps.includes('lines')) spec.stick = { ...this.makeStyleSpec(MolStyles.wireframe).stick };
        if (reps.includes('spheres')) spec.sphere = opacityOf('spheres');
        if (reps.includes('cartoon')) {
            spec.cartoon = {
                ...this.makeStyleSpec(MolStyles.cartoon).cartoon,
                ...CartoonTypeSpecs3Dmol[cartoonType],
                ...opacityOf('cartoon'),
            };
        } else if (reps.includes('ribbon')) {
            spec.cartoon = { ...this.makeStyleSpec(MolStyles.ribbons).cartoon };
        }
        return spec;
    }

    makeStyleSpec(style) {
        const reps = repsOfStyle(style);
        if (reps) return this.makeRepSetSpec(reps, repSetOptions(style));
        const singleBondRadius2D = 0.2;
        const doubleBondRadius2D = 0.1;

        // If you want to combine clicksphere with one of these, add it first in the spec object.
        switch (style) {
            case 'hidden': return {};
            case 'surface': return {};
            // case 'wireframe':    return {line:{linewidth: 5.0}};
            case 'wireframe': return { stick: { radius: 0.05 } };
            case 'sticks': return { stick: {} };
            case 'invisible': return { clicksphere: {} };
            case 'ballandstick': return { sphere: { scale: 0.3 }, stick: {} };
            case 'ghost': return { stick: { opacity: 0.8 } };
            case 'spacefill': return { sphere: {} };
            case 'ribbons': return { cartoon: {} };
            case 'backbone': return { cartoon: { trace: true } };
            case 'cartoon': return {
                cartoon: {
                    style: 'rectangle', arrows: true, color: 'spectrum',
                },
            };
            case '2dview': return { stick: { radius: singleBondRadius2D } };
            case 'tribbons': return {
                cartoon: {
                    style: 'rectangle', opacity: 0.8, color: 'spectrum',
                },
            };
            default:
                console.warn(`Unknown molecule style: ${style}`);
                return { stick: {} };
        }
    }

    // key can be a keyword or a color
    getCartoonColor(key) {
        switch (key) {
            case 'selected': return SelectionColor;
            case 'spectrum': return 'spectrum';
            case 'default':
                console.error("getCartoonColor: default isn't a valid color key. Defaulting to spectrum instead of default chain color.");
                return 'spectrum';
            default:
                // Is some representation of a color
                return key;
        }
    }

    /**
     * Get the color value for the provided chain id
     */
    getCartoonColorForChain(chain) {
        if (this.state.colorByChainId && this.state.proteinChains.includes(chain)) {
            // key is a protein chain we've identified in annotateStructureFeatures()
            return GetChainColor(chain, this.state.proteinChains);
        } else { // chainID for a non-protein element
            return 'spectrum';
        }
    }

    /**
     * Apply a color to an existing cartoon spec or create a new one
     * @param {*} key - A color or color keyword (eg selected, spectrum)
     * @param {*} cartoon - A style spec object for a catroon
     * @returns The modified cartoon style spec object
     */
    colorCartoon(key, cartoon=this.makeStyleSpec('cartoon')) {
        cartoon.cartoon.color = this.getCartoonColor(key);
        return cartoon;
    }

    /**
     * Apply the default color for a chain id to an existing cartoon spec or create a new one.
     * @param {string} chain - A chain id to use to look up a color
     * @param {StyleSpec} cartoon - A style spec object for a cartoon
     * @returns {StyleSpec} The modified cartoon style spec object
     */
    colorCartoonByChain(chain, cartoon) {
        const color = this.getCartoonColorForChain(chain);
        return this.colorCartoon(color, cartoon);
    }

    /* Workaround for 3Dmol issue: https://github.com/3dmol/3Dmol.js/issues/367
    3Dmol will spike memory if we have 1000+ unrendered spheres, crashing a Chrome tab at 1021.
    So we need to add and render water halos in chunks.
    We'll just rerender every 600 spheres.
    This function is called after adding a sphere, so just rerender if
    the current number of spheres is a multiple of 600.
    */
    sphereCrashWorkaround_Add(chunkSize=600) {
        this.state.sphereCount++;

        if (this.state.sphereCount % chunkSize === 0) {
            this.viewer.render();
        }
    }

    sphereCrashWorkaround_Remove() {
        this.state.sphereCount--;
    }
}
